/**
 * Readiness blockers gathered into the actions that clear them, so eight
 * modules short of assessments read as one thing to do rather than eight.
 *
 * @module components/shared/readiness-groups
 */

import { countLabel } from "@/components/ui";
import type { ReadinessBlocker } from "@/server/types";

/** Blockers one action clears: the same requirement, fixed in the same section. */
export interface ReadinessBlockerGroup {
  /** Stable across reads, and unique within one list. */
  key: string;
  requirement: ReadinessBlocker["requirement"];
  /** The action for a group of modules, or the lone blocker's own message. */
  label: string;
  /** Distinct modules the group names; below two, the group is one blocker. */
  moduleCount: number;
  blockers: ReadinessBlocker[];
}

/** What each module in a group still needs, by the section that clears it. */
const MODULE_ACTIONS: Record<string, string> = {
  assessments: "need their assessments completed",
  mimlos: "need module outcomes",
  "effort-hours": "need effort hours",
};

function moduleAction(section: string | undefined): string {
  return (
    (section ? MODULE_ACTIONS[section] : undefined) ??
    `need attention in ${section ?? "the programme"}`
  );
}

/**
 * Groups blockers spread over two or more modules by requirement and target
 * section, in first-seen order. Anything else stays one blocker per group, so
 * a programme-level finding keeps its own message.
 */
export function groupBlockersByAction(
  blockers: readonly ReadinessBlocker[],
): ReadinessBlockerGroup[] {
  const byAction = new Map<string, ReadinessBlocker[]>();
  blockers.forEach((blocker) => {
    const key = `${blocker.requirement}:${blocker.target?.section ?? "programme"}`;
    byAction.set(key, [...(byAction.get(key) ?? []), blocker]);
  });

  return [...byAction.entries()].flatMap(([key, members]): ReadinessBlockerGroup[] => {
    const modules = new Set(
      members.flatMap((b) => (b.target?.moduleId ? [b.target.moduleId] : [])),
    );
    if (modules.size >= 2) {
      return [
        {
          key,
          requirement: members[0].requirement,
          label: `${countLabel(modules.size, "module")} ${moduleAction(members[0].target?.section)}`,
          moduleCount: modules.size,
          blockers: members,
        },
      ];
    }
    return members.map((blocker) => ({
      key: `${key}:${blocker.target?.moduleId ?? ""}:${blocker.message}`,
      requirement: blocker.requirement,
      label: blocker.message,
      moduleCount: modules.size,
      blockers: [blocker],
    }));
  });
}
